import { closeIconV2 } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { CustomButton } from '@/shared/ui/components';
import { checkToFormatPhoneNumber, formatPhoneNumber } from '@/shared/lib';
import { Drawer } from 'antd';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { getAge } from '../../../lib/solutionsDrawer';
import { useSolutionsDrawer } from './useSolutionsDrawer';
import type { SolutionsDrawerProps } from './drawers.types';
import '../style.scss';

// generateIssueFound's stepsToTry infers as a huge union of the exact object literals in
// artifactFindings.ts (each step has a different subset of these fields), so this describes
// the real shape used below instead of that union.
interface Step {
  order: number;
  content: string;
  image?: string;
  isPatientInfo?: boolean;
  isCustomSubContent?: boolean;
  subContent?: (
    | string
    | { text?: string; image?: string; hyperText?: string; hyperLink?: string }
  )[];
}

/**
 * Renders the Resolve Issue drawer that walks a user through the troubleshooting steps for a flagged study issue. Takes the issue text, description and ordered steps from useSolutionsDrawer, renders each step with its optional image and either a plain or custom sub-content list carrying text, images and external links, and embeds the patient contact block on steps flagged with isPatientInfo.
 *
 * @param isOpen - Whether the drawer is visible.
 * @param onClickClose - Callback invoked to close the drawer.
 * @param data - The flagged issue data, including the patient used by the embedded contact block.
 * @returns The solutions drawer element.
 */
const SolutionsDrawer = ({ isOpen, onClickClose, data }: SolutionsDrawerProps) => {
  const { state } = useSolutionsDrawer({ data });

  /**
   * Renders the patient block embedded in a solutions step, showing name, gender initial, age and both contact numbers. Decides from the patient's contact country whether the phone numbers are run through formatPhoneNumber, and falls back to placeholder text for missing values.
   *
   * @returns The rendered patient information block.
   */
  const patientInfoRenderer = () => {
    const shouldFormatPhoneNumber = checkToFormatPhoneNumber(data?.patient?.contact?.country);
    return (
      <div className="patient-info">
        <div className="patient-name">
          {`${data?.patient?.name || '--'} (${data?.patient?.gender.charAt(0) || ''} - ${getAge(data?.patient?.dob)})`}
        </div>
        <div className="patient-phone-number">
          <div className="patient-phone-number-item">
            <div className="label">{t`Primary phone number:`}</div>
            <div className="value">
              {(shouldFormatPhoneNumber
                ? formatPhoneNumber(data?.patient?.contact.phone1)
                : data?.patient?.contact.phone1) || DOUBLE_HYPHEN_BULLET}
            </div>
          </div>
          <div className="patient-phone-number-item">
            <div className="label">{t`Secondary phone number:`}</div>
            <div className="value">
              {(shouldFormatPhoneNumber
                ? formatPhoneNumber(data?.patient?.contact.phone2)
                : data?.patient?.contact.phone2) || DOUBLE_HYPHEN_BULLET}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      open={isOpen}
      aria-label={t`Resolve Issue`}
      closable={false}
      width={500}
      rootClassName="solutions-drawer"
      onClose={onClickClose}
    >
      <div className="header-container">
        <span className="header">{t`Resolve Issue`}</span>
        <CustomButton
          id="close-btn"
          icon={<ReactSVG src={closeIconV2} />}
          type="tertiary"
          style="neutral"
          onClick={onClickClose}
          ariaLabel={t`Close`}
        />
      </div>
      <div className="body-container">
        <div className="issue-container">
          <div className="issue">{state.issueFound}</div>
          <div className="description">{state.description}</div>
        </div>
        <div className="steps-container">
          <div className="header">{t`Steps to try`}</div>
          <div className="body">
            {_.map(state.stepsToTry as Step[], (step) => (
              <div className="step-row">
                <div className="step-row__header">
                  <div className="number">{step.order}</div>
                  <div>{step.content}</div>
                </div>
                <div className="content">
                  <div>
                    {!!step.image && <img src={step.image} alt="Sub content" />}
                    {step.subContent &&
                      step.subContent.length > 0 &&
                      (step.isCustomSubContent ? (
                        <ul className="custom-sub-content">
                          {_.map(
                            step.subContent,
                            (item: {
                              text?: string;
                              image?: string;
                              hyperText?: string;
                              hyperLink?: string;
                            }) => (
                              <li>
                                <div>{item?.text}</div>
                                {item?.image && <img src={item.image} alt="Sub content" />}
                                {item?.hyperText && item?.hyperLink && (
                                  <a
                                    href={item.hyperLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {item.hyperText}
                                  </a>
                                )}
                              </li>
                            ),
                          )}
                        </ul>
                      ) : (
                        <ul>
                          {_.map(step.subContent, (item) => (
                            <li>{item as string}</li>
                          ))}
                        </ul>
                      ))}
                  </div>
                  {step.isPatientInfo && patientInfoRenderer()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SolutionsDrawer;
